import { Injectable } from '@angular/core';
import { StorageService, UsersService } from '../services';
import { initialUserState } from '../store/initialisation/users';
import { IUser } from 'src/app/models';
import { CommunicationUserType, UserSortType, UserStatus, UserType } from 'src/app/models/enums';
import { objectToCamel } from 'ts-case-convert';
import { initialPaginationState } from '../store/initialisation/pagination';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { APP } from '../config/app';
import {
  IUsersFilters,
  deserializeUsersFilters,
  parseUsersFiltersFromStorage,
} from '../filters';

@Injectable({
  providedIn: 'root',
})
export class UsersResolver {
  // Tracks the latest list-fetch request; stale completions are dropped so that
  // a fast hydrate-driven re-resolve isn't overwritten by the slower initial one.
  private listGeneration = 0;
  // Fingerprint of the inputs of the last successful list fetch. Skips redundant
  // fetches when navigating between /users and /users/:id (sibling routes always
  // re-run the resolver).
  private lastFetchFingerprint: string | null = null;
  // Fingerprint without page. Lets us tell a pure page change from a filter
  // change so the pagination only collapses to "currentPage ···" when totals
  // are genuinely unknown.
  private lastFilterFingerprint: string | null = null;

  constructor(
    private usersService: UsersService,
    public storageService: StorageService,
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async resolve(route: ActivatedRouteSnapshot, state?: RouterStateSnapshot) {
    const listType = this.resolveListType(route) === UserType.Driver ? CommunicationUserType.DRIVERS : CommunicationUserType.USERS;
    this.storageService.setUsersLastListType(listType);
    this.storageService.setRouteName(listType);
    // Read page and size from URL query params first
    const pageParam = route.queryParamMap.get('page');
    const pageFromUrl = pageParam ? Number(pageParam) : null;
    const page = (pageFromUrl && !isNaN(pageFromUrl) && pageFromUrl > 0)
      ? pageFromUrl
      : (route.data['users']?.pagination.actualPage || 1);
    const sizeParam = route.queryParamMap.get('size');
    const sizeFromUrl = sizeParam ? Number(sizeParam) : null;
    const size = (sizeFromUrl && !isNaN(sizeFromUrl) && sizeFromUrl > 0)
      ? sizeFromUrl
      : APP.DEFAULT_PAGE_SIZE;
    const search = route.queryParamMap.get('search') ?? undefined;
    const sortParam = route.queryParamMap.get('sort');
    const filters = await this.resolveFilters(route);
    const listUserType = this.resolveListType(route);
    const userSortType = sortParam
      ? (sortParam as UserSortType)
      : (await this.storageService.getUserSortType() ?? UserSortType.CREATION);

    const fingerprint = this.buildFingerprint(size, page, userSortType, filters, listUserType, search);
    if (fingerprint === this.lastFetchFingerprint && initialUserState.value?.length) {
      return;
    }

    initialUserState.isFetchingUsers = true;
    initialUserState.value = [];
    const filterFingerprint = this.buildFingerprint(size, 0, userSortType, filters, listUserType, search);
    if (filterFingerprint !== this.lastFilterFingerprint) {
      initialUserState.pagination.totalPages = 0;
      initialUserState.pagination.totalElements = 0;
    }
    this.lastFilterFingerprint = filterFingerprint;
    this.resolveAsync(size, page, userSortType, filters, listUserType, search, fingerprint);
  }

  private resolveListType(route: ActivatedRouteSnapshot): UserType {
    return route.routeConfig?.path?.split('/')[0] === 'drivers' ? UserType.Driver : UserType.Regular;
  }

  private buildFingerprint(
    size: number,
    page: number,
    sortType: UserSortType,
    filters: IUsersFilters,
    listUserType: UserType,
    search: string | undefined,
  ): string {
    return JSON.stringify({
      size,
      page,
      sortType,
      listUserType,
      search: search ?? null,
      clients: [...filters.selectedClients].sort(),
      towns: [...filters.selectedTowns].sort(),
      operators: [...filters.selectedOperators].sort(),
      status: [...filters.selectedStatus].sort(),
    });
  }

  private async resolveFilters(route: ActivatedRouteSnapshot): Promise<IUsersFilters> {
    // URL is the source of truth whenever there is *any* query param. Storage is only
    // consulted on a truly bare URL (the "sidebar tab-switch" entry path).
    if (route.queryParamMap.keys.length > 0) {
      return deserializeUsersFilters(route.queryParamMap);
    }
    return parseUsersFiltersFromStorage(await this.storageService.getUsersFilters()) ?? {
      selectedClients: [],
      selectedTowns: [],
      selectedOperators: [],
      selectedStatus: [],
    };
  }

  async resolveAsync(
    size: number,
    page: number,
    userSortType: UserSortType,
    filters: IUsersFilters,
    listUserType: UserType,
    search?: string,
    fingerprint?: string,
  ) {
    const myGen = ++this.listGeneration;
    const {
      content,
      numberOfElements,
      totalElements,
      totalPages,
      first,
      last,
    } = await this.fetchByListType(userSortType, page, size, filters, listUserType, search);
    // Drop the response if a newer resolver call has started in the meantime.
    if (myGen !== this.listGeneration) return;
    const result = this.getData(content, numberOfElements, totalElements, totalPages, first, last, page, size);
    if (fingerprint !== undefined) this.lastFetchFingerprint = fingerprint;
    return result;
  }

  async filter(
    route: ActivatedRouteSnapshot,
    clientsSelected: number[],
    townsSelected: number[],
    operatorsSelected: number[],
    statusSelected: UserStatus[],
    searchValue?: string,
  ) {
    const page = route.data['users']?.pagination.actualPage || 1;
    const savedPageSize = await this.storageService.getUsersPageSize();
    const size = route.data['users']?.pagination.size || savedPageSize || APP.DEFAULT_PAGE_SIZE;
    const userSortType = await this.storageService.getUserSortType() ?? UserSortType.CREATION;
    const filters: IUsersFilters = {
      selectedClients: clientsSelected,
      selectedTowns: townsSelected,
      selectedOperators: operatorsSelected,
      selectedStatus: statusSelected,
    };
    const {
      content,
      numberOfElements,
      totalElements,
      totalPages,
      first,
      last,
    } = await this.fetchByListType(userSortType, page, size, filters, this.resolveListType(route), searchValue);
    return this.getData(
      content,
      numberOfElements,
      totalElements,
      totalPages,
      first,
      last,
      page,
      size,
    );
  }

  private fetchByListType(
    userSortType: UserSortType,
    page: number,
    size: number,
    filters: IUsersFilters,
    listUserType: UserType,
    searchValue?: string,
  ) {
    return this.usersService.filterByUserType(
      listUserType, userSortType, page, size,
      filters.selectedClients, filters.selectedTowns, filters.selectedOperators, filters.selectedStatus,
      searchValue,
    );
  }

  getData = (
    content: any,
    number_of_elements: any,
    total_elements: any,
    total_pages: any,
    first: any,
    last: any,
    page: any,
    size: number = APP.DEFAULT_PAGE_SIZE,
  ) => {
    const data = initialUserState;
    data.value = objectToCamel(content) as Array<IUser>;
    data.isFetchingUsers = false;
    data.pagination = {...initialPaginationState};
    data.pagination.numberOfElements = number_of_elements;
    data.pagination.totalPages = total_pages;
    data.pagination.totalElements = total_elements;
    data.pagination.actualPage = page;
    data.pagination.first = first;
    data.pagination.last = last;
    data.pagination.size = size;
    return data;
  };
}
